import React, { useMemo } from 'react';
import classnames from 'classnames';
import { Table, TableAddon } from 'tea-component';

import type { IRecord } from '@home/types/common';

const { autotip } = Table.addons;

export interface IUseTableAutotipOptions {
  style?: React.CSSProperties;
  className?: string;
  // 数据是否在加载中
  isLoading?: boolean;
  isUseResetNodata?: boolean;
  // 加载文案
  loadingText?: string;
  // 数据加载是否异常
  isError?: boolean;
  // 错误时的提示文案
  errorText?: React.ReactNode;
  // 重试文案 在 status = “error" 且包含 onRetry 时展现
  retryText?: React.ReactNode;
  // status = "error" 时，用户点击 retryText 时回调。如果传空，则不渲染重试操作
  onRetry?: () => void;
  // 数据是否被筛选
  isFound?: boolean;
  // 找到结果时的文案
  foundText?: React.ReactNode;
  // 清空结果提示文案 在 status = found" 且包含 onClear 时展现
  clearResultText?: React.ReactNode;
  // status = "found" 时，用户点击 clearResultText 时回调。如果传空，则不渲染返回操作
  onClear?: () => void;
  // 是否隐藏图标
  hideIcon?: boolean;
  // 空数据提示文案
  emptyText?: React.ReactNode;
}

const HEIGHT = 200 - 14 - 14;

export default function useTableAutotipAddon<T = IRecord>(props: IUseTableAutotipOptions) {
  const {
    style,
    className,
    // 数据是否在加载中
    isLoading = false,
    // 是否使用Reset无数据提示内容
    isUseResetNodata = false,
    // 加载文案
    loadingText = 'Loading...',
    // 数据加载是否异常
    isError = false,
    // 错误时的提示文案
    errorText = <span style={{ color: '#444' }}>Loading failed, please try</span>,
    // 重试文案 在 status = “error" 且包含 onRetry 时展现
    retryText = <span style={{ color: '#326ff0' }}>again</span>,
    // status = "error" 时，用户点击 retryText 时回调。如果传空，则不渲染重试操作
    onRetry,
    // 数据是否被筛选
    isFound,
    // 找到结果时的文案
    foundText = '',
    // 清空结果提示文案 在 status = found" 且包含 onClear 时展现
    clearResultText = 'Back',
    // status = "found" 时，用户点击 clearResultText 时回调。如果传空，则不渲染返回操作
    onClear,
    // 是否隐藏图标
    hideIcon = !isLoading,
    // 空数据提示文案
    emptyText = '',
  } = props;

  // console.log('useTableAutotipAddon', props);

  // noData文案设置-最新统一foundTextCur
  const foundTextCur = useMemo(() => {
    if (isUseResetNodata) {
      return (
        <div className="dbg-nodata-div">
          <span className="dbg-nodata-normal-span">{`No data found for the selected filters. Please adjust your filters or click '`}</span>
          <span className="dbg-nodata-reset-a-span" onClick={onClear}>
            {'Reset'}
          </span>
          <span className="dbg-nodata-normal-span">{`' to turn all filters off.`}</span>
        </div>
      );
    }
    return foundText;
  }, [isUseResetNodata, foundText, onClear]);

  // noData文案设置-最新统一clearResultTextCur
  const clearResultTextCur = useMemo(() => {
    if (isUseResetNodata) {
      return ' ';
    }
    return clearResultText;
  }, [isUseResetNodata, clearResultText]);

  const autotipAddon = useMemo<TableAddon<T>>(() => {
    return autotip({
      style: { ...style, height: HEIGHT, lineHeight: `${HEIGHT}px`, userSelect: 'none' },
      className: classnames('', className),

      // 数据是否在加载中
      isLoading,
      // 加载文案
      loadingText,

      // 数据加载是否异常
      isError: !isLoading && isError,
      // 错误时的提示文案
      errorText,
      // 重试文案 在 status = “error" 且包含 onRetry 时展现
      retryText,
      // status = "error" 时，用户点击 retryText 时回调。如果传空，则不渲染重试操作
      onRetry,

      // 数据是否被筛选
      isFound: !isLoading && isFound,
      foundText: foundTextCur,
      // 清空结果提示文案 在 status = found" 且包含 onClear 时展现
      clearResultText: clearResultTextCur,
      // status = "found" 时，用户点击 clearResultText 时回调。如果传空，则不渲染返回操作
      onClear,

      // 是否隐藏图标
      hideIcon,

      // 空数据提示文案
      emptyText,
    });
  }, [
    // 加载中
    isLoading,
    loadingText,

    // 异常
    isError,
    errorText,
    retryText,
    onRetry,

    // 未匹配到数据
    isFound,
    foundText,
    clearResultText,
    onClear,

    // 是否隐藏图标
    hideIcon,

    // 空数据提示文案
    emptyText,
  ]);

  return autotipAddon;
}
